/**
 * Master-solution GENERATION — ONE JOB PER TOP-LEVEL MAIN TASK.
 *
 * This is the type the per-task granularity was chosen for, and the agent already worked this way: one LLM
 * call per root subtree, so the model stays coherent across a task's subtasks and can distribute a stated
 * parent point total. `generateMasterSolutionForRootTask` makes one of those calls addressable.
 *
 * WHAT THE SPLIT REPLACES. `runMasterSolutionAgent` holds a live `watchQuery` on the exam for the duration of
 * the run, starting each root's subtree as the structure extraction creates it and marking the run finished
 * when none are left. That subscription is per-process and dies with it: a restart mid-run leaves the
 * remaining roots ungenerated and the progress bar stuck. As rows, the outstanding roots are on disk with a
 * lease each, so a restart resumes them.
 *
 * NOT re-derived, though: `dispatchMasterSolutionGeneration` enumerates the roots once, at the request. The
 * subscription's other property — noticing roots that appear LATER — is therefore gone, which is safe only
 * because generation is requested after the structure is confirmed and the tree is fixed by then.
 *
 * It also replaces the `inFlightTaskKeys` Set the agent uses to stop the subscription from double-starting a
 * root. `dedupeKey` is the same guard enforced by a unique index, so it holds across instances rather than
 * within one process.
 *
 * ROOTS RUN IN PARALLEL, and that took a change to how a root is persisted. Each one used to read
 * `exam.tasks`, swap its own subtree into a copy and write the WHOLE array back, so two roots running at once
 * lost one another's work outright. `persistRootSubtree` now writes `tasks.$[root]` positionally, matched on
 * the root's `_id`, so a root touches only its own element — which is what makes the per-root `lockKey` below
 * safe rather than merely appealing.
 */

import { getCollection } from '@adornis/baseql/server/collections.js';
import { Exam } from '../../../exam.js';
import {
  generateMasterSolutionForRootTask,
  openMasterSolutionGenerationRun,
} from '../../agents/ms-generation-agent.js';
import { getTaskKey, msTargets, type NestedExamTask } from '../../agents/tree-generation-utils.js';
import { masterSolutionStep } from '../steps/master-solution.step.js';
import * as conditions from '../job-conditions.js';
import { taskTreeLockKey } from '../job.js';
import { defineJob } from '../registry.js';

export interface MsGenerationPayload {
  /** `getTaskKey` of the top-level main task this job generates. The persisted task `_id` when there is one. */
  taskKey: string;
  /** Every gradable node under this root this call targets (`getTaskKey` per node), computed once here at
   * dispatch time from the same `msTargets` the handler itself uses. Informational only — read by
   * `get-live-solution-jobs.ts` to drive the ms-gs canvas's per-box spinner; nothing in this job definition
   * consults it, and the handler recomputes its own targets fresh regardless. */
  targetTaskIds: string[];
  [key: string]: unknown;
}

export const masterSolutionGenerationJob = defineJob<MsGenerationPayload>({
  type: 'msGeneration',

  /**
   * ONE ROW PER ROOT that still has a task needing a master solution.
   *
   * ENUMERATED, NOT DERIVED, and the difference cost this system a whole file. Both generation types used to be
   * invented by a reconcile pass: a tool opened the run and called `examChanged`, and `reconcileExam` read the roots
   * off the exam through a `desired(exam)` on each definition. Enumerating them in the dispatch is what let all of
   * that go — the reconcile file, the `desired` field, and the run id the rows had to read off the progress record
   * because a caller-less enqueue had nothing else to read.
   *
   * It also gives up the one thing derivation was good for, and that is worth being explicit: nothing re-derives
   * these rows later. A lost enqueue stays lost until the teacher asks again, and a root created by a structure
   * re-extraction mid-run never gets a job. In practice the derivation was only ever reached from the same two tool
   * calls — no periodic reconcile ever existed — so it was already one-shot; this makes that honest.
   *
   * Roots that already have their solutions are skipped rather than enqueued and dropped: `ready()` would drop them
   * as `already-covered` anyway, and the run's `total` counts every root regardless (see the opener).
   */
  /**
   * A generation is never a repeat: it fills gaps in an artifact rather than re-delivering it, so `reupload: false`.
   * `open` writes the producer, the message and the root total.
   */
  onStepStart: { reupload: false, open: examId => openMasterSolutionGenerationRun(examId) },

  batch: {
    units: async examId => {
      const examCollection = await getCollection(Exam);
      const exam = await examCollection.findOne({ _id: examId });
      const roots = ((exam?.tasks ?? []) as NestedExamTask[]).filter(root => msTargets(root).length > 0);
      return roots.map(root => ({
        taskKey: getTaskKey(root),
        targetTaskIds: msTargets(root).map(s => getTaskKey(s.node)),
      }));
    },
  },

  /**
   * Closing the run used to be `completeRunIfDone` here:  /**
   * The run is supplied by `dispatchMasterSolutionGeneration`. Closing it used to be `completeRunIfDone` here:
   * every root's handler re-read the exam and re-walked the task tree to ask whether any root still needed
   * work. The queue answers that from the rows — "is any `masterSolution` job still live for this exam?" — and
   * `masterSolutionStep` carries over the one thing the rows cannot show (task extraction must have finished,
   * or more roots are still coming).
   */
  onStepComplete: masterSolutionStep,

  /**
   * PER ROOT, not the shared task-tree key: a root's generation writes only its own `tasks.$[root]` element.
   *
   * A root with no `_id` keeps the SERIAL key, and the reason changed: there is no longer a whole-array fallback
   * for it to serialise against. `persistTaskArtifact` addresses one `_id` per level, so a root without one is
   * unaddressable and its write is SKIPPED (loudly — see the warning there), as is everything under it. The serial
   * key is what is left that is honest: a row that cannot say which element it writes must not be told it may run
   * beside other writers of the same array. `getTaskKey`'s fallback shape (`task:<taskNumber>`) is exactly that
   * signal, which is why the key is derived from the payload rather than assumed.
   */
  lockKey: (examId, payload) =>
    payload.taskKey.startsWith('task:') ? taskTreeLockKey(examId) : `${taskTreeLockKey(examId)}:${payload.taskKey}`,

  // Per ROOT: two roots of one exam are genuinely separate work and must both exist. Leaving this at the
  // default (`msGeneration:<examId>`) would silently drop every root after the first.
  dedupeKey: (examId, payload) => `msGeneration:${examId}:${payload.taskKey}`,

  ready: (exam, job) => {
    if (!conditions.hasTasks(exam)) return { verdict: 'wait', because: 'msGeneration:no-tasks' };

    // The one collision the field-scoped writes do NOT resolve: extraction and generation are two producers
    // of the SAME field. Their lock keys differ (per-root vs per-artifact) so nothing else keeps them apart,
    // and it is a domain conflict before it is a write conflict — a teacher uploading a master solution while
    // one is being generated wants the upload. Wait; the extraction will fill these roots and `ready` will
    // then drop this job as already-covered.
    if (conditions.artifactExtractionRunning(exam, 'masterSolution')) {
      return { verdict: 'wait', because: 'msGeneration:extraction-running' };
    }

    const root = ((exam.tasks ?? []) as unknown as NestedExamTask[]).find(
      task => getTaskKey(task) === job.payload.taskKey,
    );
    // The structure was rebuilt and this root no longer exists. Reconcile creates jobs for whatever replaced
    // it, so this row has nothing left to do.
    if (!root) return { verdict: 'drop', because: 'msGeneration:root-gone' };
    // Filled in the meantime — by another run, by an uploaded solution, or by the teacher.
    if (msTargets(root).length === 0) return { verdict: 'drop', because: 'msGeneration:already-covered' };

    return 'run';
  },

  /**
   * One root's subtree, however many subtasks it has. Well under the extraction budget because it is a single
   * model call plus its writes — and a tighter budget is what makes crash recovery for this type proportional
   * to its own cost rather than to the slowest job in the system.
   */

  maxRuntimeMs: 10 * 60_000,
  maxAttempts: 3,

  // NOT DERIVED. These rows are enqueued by `dispatchMasterSolutionGeneration`, which enumerates the roots
  // itself. Deriving them meant a reconcile pass invented rows from exam state with no caller to ask, which is
  // what forced a run id onto the progress record — a caller-less enqueue had nothing else to read. The trade
  // is stated in the dispatcher: nothing re-derives a row that goes missing. It was already a one-shot
  // (nothing but the two generation tools ever reconciled an exam), so this makes that visible rather than
  // changing it.

  // Closing the run is NOT done here. A handler runs before its own row is written to a terminal status, so a
  // completion check from inside it always counts ITSELF as live and the last root could never close the run.
  // `onTransition` above fires after that write. See `step-completion.ts`.
  handler: async (examId, payload) => {
    const result = await generateMasterSolutionForRootTask(examId, payload.taskKey);
    if (!result.generated) return { skipped: true, note: result.skipped ?? 'nothing to generate' };
    return { note: 'generated' };
  },
});
